<script>
import axios from "axios";
import { BrandsPath, GravyPath, PackagingPath } from "../../../../../path";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_BACKEND_URL,
      brands: [],
      selectedBrandId: null,
      brandKit: [],
      loading: false,
      // Min qty inline edit
      editingId: null,
      editingQty: 0,
      saving: false,

      // -- Gravy Add Modal --
      showGravyModal: false,
      modalGravyFilter: "",
      modalGravies: [],
      modalGravyLoading: false,
      addingGravyId: null,
      modalGravyDebounceTimer: null,
      modalGravyCurrentPage: 1,
      modalGravyTotalRows: 0,
      modalGravyPerPage: 15,

      // -- Packaging Add Modal --
      showPackagingModal: false,
      modalPackagingFilter: "",
      modalPackagings: [],
      modalPackagingLoading: false,
      addingPackagingId: null,
      modalPackagingDebounceTimer: null,
      modalPackagingCurrentPage: 1,
      modalPackagingTotalRows: 0,
      modalPackagingPerPage: 15,
    };
  },
  computed: {
    brandOptions() {
      return [
        { value: null, text: "-- Select a Brand --" },
        ...this.brands.map((b) => ({ value: b.id, text: b.name })),
      ];
    },
    // Filter the general brandKit into two based on presence of gravy or packaging relation
    gravyKit() {
      return this.brandKit.filter((k) => k.gravy !== null);
    },
    packagingKit() {
      return this.brandKit.filter((k) => k.packaging !== null);
    },
    // IDs for exclusion in modals
    existingGravyIds() {
      return this.gravyKit.map((k) => k.gravy_id);
    },
    existingPackagingIds() {
      return this.packagingKit.map((k) => k.packaging_id);
    },
  },
  methods: {
    async getAllBrands() {
      try {
        let res = await axios.get(this.baseUrl + BrandsPath.GET_BRANDS);
        if (res.data.status) {
          this.brands = res.data.brandsList;
        }
      } catch (e) {
        console.error("Error fetching brands:", e);
      }
    },
    async fetchBrandKit() {
      if (!this.selectedBrandId) return;
      try {
        this.loading = true;
        let res = await axios.get(this.baseUrl + BrandsPath.BRAND_KIT, {
          params: { "brand_ids[]": [this.selectedBrandId] },
        });
        if (res.data.status) {
          // Keep all, then they get filtered by computed properties
          this.brandKit = res.data.data;
        }
      } catch (e) {
        console.error("Error fetching brand kit:", e);
      } finally {
        this.loading = false;
      }
    },

    // ── Min Qty inline edit ───────────────────────────────────────────────
    startEdit(item) {
      this.editingId = item.id;
      this.editingQty = item.min_quantity;
    },
    cancelEdit() {
      this.editingId = null;
      this.editingQty = 0;
    },
    async saveEdit(item) {
      try {
        this.saving = true;
        let res = await axios.post(
          this.baseUrl + BrandsPath.UPDATE_BRAND_KIT_MIN_QTY,
          { id: item.id, min_quantity: this.editingQty }
        );
        if (res.data.status) {
          item.min_quantity = this.editingQty;
          this.editingId = null;
          Swal.fire("Updated!", "Min quantity saved.", "success");
        } else {
          Swal.fire("Error", res.data.message || "Update failed", "error");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Something went wrong.", "error");
      } finally {
        this.saving = false;
      }
    },

    // ── Gravy Modal Logic ──────────────────────────────────────────────────
    openGravyModal() {
      this.showGravyModal = true;
      this.modalGravyFilter = "";
      this.modalGravyCurrentPage = 1;
      this.loadModalGravies();
    },
    onGravyFilterInput() {
      clearTimeout(this.modalGravyDebounceTimer);
      this.modalGravyCurrentPage = 1;
      this.modalGravyDebounceTimer = setTimeout(() => this.loadModalGravies(), 300);
    },
    onGravyPageChange(page) {
      this.modalGravyCurrentPage = page;
      this.loadModalGravies();
    },
    async loadModalGravies() {
      try {
        this.modalGravyLoading = true;
        let res = await axios.get(this.baseUrl + GravyPath.SEARCH_GRAVIES, {
          params: {
            search: this.modalGravyFilter,
            page: this.modalGravyCurrentPage,
            per_page: this.modalGravyPerPage,
          },
        });
        if (res.data.status) {
            this.modalGravies = res.data.data;
            this.modalGravyTotalRows = res.data.data.length;
            this.modalGravyCurrentPage = 1;
        }
      } catch (e) {
        console.error("Error loading gravies:", e);
      } finally {
        this.modalGravyLoading = false;
      }
    },
    isGravyInKit(gravyId) {
      return this.existingGravyIds.includes(gravyId);
    },
    getGravyKitItem(gravyId) {
      return this.gravyKit.find(k => k.gravy_id === gravyId);
    },
    async addGravyToKit(gravy) {
      const result = await Swal.fire({
        title: "Add Gravy?",
        text: `Add "${gravy.name}" to this brand kit?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, add",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;

      try {
        this.addingGravyId = gravy.id;
        let res = await axios.post(this.baseUrl + BrandsPath.ADD_BRAND_KIT_GRAVY, {
          brand_id: this.selectedBrandId,
          gravy_id: gravy.id,
          min_quantity: 0
        });
        if (res.data.status) {
          this.brandKit.push(res.data.data);
          Swal.fire("Added!", `"${gravy.name}" has been added to the brand kit.`, "success");
        } else {
          Swal.fire("Error", res.data.message || "Could not add gravy", "error");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Something went wrong.", "error");
      } finally {
        this.addingGravyId = null;
      }
    },

    // ── Packaging Modal Logic ──────────────────────────────────────────────
    openPackagingModal() {
      this.showPackagingModal = true;
      this.modalPackagingFilter = "";
      this.modalPackagingCurrentPage = 1;
      this.loadModalPackagings();
    },
    onPackagingFilterInput() {
      clearTimeout(this.modalPackagingDebounceTimer);
      this.modalPackagingCurrentPage = 1;
      this.modalPackagingDebounceTimer = setTimeout(() => this.loadModalPackagings(), 300);
    },
    onPackagingPageChange(page) {
      this.modalPackagingCurrentPage = page;
      this.loadModalPackagings();
    },
    async loadModalPackagings() {
      try {
        this.modalPackagingLoading = true;
        let res = await axios.get(this.baseUrl + PackagingPath.SEARCH_PACKAGING, {
          params: {
            search: this.modalPackagingFilter,
            page: this.modalPackagingCurrentPage,
            per_page: this.modalPackagingPerPage,
          },
        });
        if (res.data.status) {
            this.modalPackagings = res.data.data;
            this.modalPackagingTotalRows = res.data.data.length;
            this.modalPackagingCurrentPage = 1;
        }
      } catch (e) {
        console.error("Error loading packagings:", e);
      } finally {
        this.modalPackagingLoading = false;
      }
    },
    isPackagingInKit(packagingId) {
      return this.existingPackagingIds.includes(packagingId);
    },
    getPackagingKitItem(packagingId) {
      return this.packagingKit.find(k => k.packaging_id === packagingId);
    },
    async addPackagingToKit(packaging) {
      const result = await Swal.fire({
        title: "Add Packaging?",
        text: `Add "${packaging.name}" to this brand kit?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, add",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;

      try {
        this.addingPackagingId = packaging.id;
        let res = await axios.post(this.baseUrl + BrandsPath.ADD_BRAND_KIT_PACKAGING, {
          brand_id: this.selectedBrandId,
          packaging_id: packaging.id,
          min_quantity: 0
        });
        if (res.data.status) {
          this.brandKit.push(res.data.data);
          Swal.fire("Added!", `"${packaging.name}" has been added to the brand kit.`, "success");
        } else {
          Swal.fire("Error", res.data.message || "Could not add packaging", "error");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Something went wrong.", "error");
      } finally {
        this.addingPackagingId = null;
      }
    },

    // ── Remove Item Logic ──────────────────────────────────────────────────
    async removeItem(item, type) {
      const name = type === 'gravy' ? item.gravy?.name : item.packaging?.name;
      const result = await Swal.fire({
        title: `Remove ${type}?`,
        text: `Remove "${name}" from this brand kit?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
      });
      if (!result.isConfirmed) return;

      try {
        const path = type === 'gravy' ? BrandsPath.REMOVE_BRAND_KIT_GRAVY : BrandsPath.REMOVE_BRAND_KIT_PACKAGING;
        let res = await axios.post(this.baseUrl + path, { id: item.id });
        if (res.data.status) {
          const index = this.brandKit.findIndex(k => k.id === item.id);
          if (index !== -1) this.brandKit.splice(index, 1);
          if (this.editingId === item.id) this.cancelEdit();
          Swal.fire("Removed!", `"${name}" has been removed from the brand kit.`, "success");
        } else {
          Swal.fire("Error", res.data.message || "Could not remove", "error");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Something went wrong.", "error");
      }
    },
  },
  watch: {
    selectedBrandId(val) {
      this.brandKit = [];
      if (val) this.fetchBrandKit();
    },
  },
  mounted() {
    this.getAllBrands();
  },
};
</script>

<template>
  <div>
    <!-- Brand Dropdown -->
    <b-row class="mb-3">
      <b-col sm="6">
        <label for="brand-select"><strong>Select Brand</strong></label>
        <b-form-select
          id="brand-select"
          v-model="selectedBrandId"
          :options="brandOptions"
        ></b-form-select>
      </b-col>
    </b-row>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
    </div>

    <!-- Main Content Tabs -->
    <div v-if="!loading && selectedBrandId" class="mt-3">
      <b-card no-body>
        <b-tabs card>
          <!-- ── Gravy Tab ────────────────────────────────────────────── -->
          <b-tab title="Gravy" active>
            <b-row class="mb-2 align-items-center">
              <b-col>
                <h6 class="mb-0">
                  Gravy Kit Items
                  <b-badge variant="secondary" class="ml-1">{{ gravyKit.length }}</b-badge>
                </h6>
              </b-col>
              <b-col class="text-right">
                <b-button variant="primary" size="sm" @click="openGravyModal">
                  <i class="fas fa-plus"></i> Add Gravy
                </b-button>
              </b-col>
            </b-row>

            <b-table
              v-if="gravyKit.length > 0"
              :items="gravyKit"
              :fields="[
                { key: 'gravy.name', label: 'Gravy' },
                { key: 'units', label: 'Units', thStyle: 'width: 120px' },
                { key: 'min_quantity', label: 'Min Qty', thStyle: 'width: 140px' },
                { key: 'actions', label: '', thStyle: 'width: 160px' },
              ]"
              bordered responsive striped hover
            >
              <template #cell(gravy.name)="row">{{ row.item.gravy.name }}</template>

              <template #cell(units)="row">{{ row.item.gravy.uom }} {{ row.item.gravy.units }}</template>

              <template #cell(min_quantity)="row">
                <span v-if="editingId !== row.item.id">{{ row.item.min_quantity ?? 0 }}</span>
                <b-form-input
                  v-else
                  v-model.number="editingQty"
                  type="number" min="0" size="sm"
                  style="width: 90px; display: inline-block"
                ></b-form-input>
              </template>

              <template #cell(actions)="row">
                <div v-if="editingId !== row.item.id" class="d-flex" style="gap: 6px">
                  <b-button size="sm" variant="outline-primary" @click="startEdit(row.item)">
                    <i class="fas fa-pencil-alt"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="removeItem(row.item, 'gravy')">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </div>
                <div v-else class="d-flex" style="gap: 6px">
                  <b-button size="sm" variant="success" :disabled="saving" @click="saveEdit(row.item)">
                    <b-spinner v-if="saving" small></b-spinner>
                    <span v-else>Save</span>
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" @click="cancelEdit">Cancel</b-button>
                </div>
              </template>
            </b-table>
            <p v-else class="text-muted text-center mt-3">No gravies added to this brand kit.</p>
          </b-tab>

          <!-- ── Packaging Tab ────────────────────────────────────────── -->
          <b-tab title="Packaging">
            <b-row class="mb-2 align-items-center">
              <b-col>
                <h6 class="mb-0">
                  Packaging Kit Items
                  <b-badge variant="secondary" class="ml-1">{{ packagingKit.length }}</b-badge>
                </h6>
              </b-col>
              <b-col class="text-right">
                <b-button variant="primary" size="sm" @click="openPackagingModal">
                  <i class="fas fa-plus"></i> Add Packaging
                </b-button>
              </b-col>
            </b-row>

            <b-table
              v-if="packagingKit.length > 0"
              :items="packagingKit"
              :fields="[
                { key: 'packaging.name', label: 'Packaging' },
                { key: 'units', label: 'Units', thStyle: 'width: 120px' },
                { key: 'min_quantity', label: 'Min Qty', thStyle: 'width: 140px' },
                { key: 'actions', label: '', thStyle: 'width: 160px' },
              ]"
              bordered responsive striped hover
            >
              <template #cell(packaging.name)="row">{{ row.item.packaging.name }}</template>

              <template #cell(units)="row">{{ row.item.packaging.units }} {{ row.item.packaging.uom }}</template>

              <template #cell(min_quantity)="row">
                <span v-if="editingId !== row.item.id">{{ row.item.min_quantity ?? 0 }}</span>
                <b-form-input
                  v-else
                  v-model.number="editingQty"
                  type="number" min="0" size="sm"
                  style="width: 90px; display: inline-block"
                ></b-form-input>
              </template>

              <template #cell(actions)="row">
                <div v-if="editingId !== row.item.id" class="d-flex" style="gap: 6px">
                  <b-button size="sm" variant="outline-primary" @click="startEdit(row.item)">
                    <i class="fas fa-pencil-alt"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="removeItem(row.item, 'packaging')">
                    <i class="fas fa-trash"></i>
                  </b-button>
                </div>
                <div v-else class="d-flex" style="gap: 6px">
                  <b-button size="sm" variant="success" :disabled="saving" @click="saveEdit(row.item)">
                    <b-spinner v-if="saving" small></b-spinner>
                    <span v-else>Save</span>
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" @click="cancelEdit">Cancel</b-button>
                </div>
              </template>
            </b-table>
            <p v-else class="text-muted text-center mt-3">No packaging added to this brand kit.</p>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- ── Add Gravy Modal ─────────────────────────────────────────────── -->
    <b-modal
      v-model="showGravyModal"
      title="Add Gravy to Brand Kit"
      size="lg" hide-footer
    >
      <b-form-input
        v-model="modalGravyFilter"
        placeholder="Search gravies..."
        class="mb-3" @input="onGravyFilterInput"
      ></b-form-input>

      <div v-if="modalGravyLoading" class="text-center py-4"><b-spinner variant="primary"></b-spinner></div>
      <div v-else>
        <b-table
          :items="modalGravies"
          :fields="[
            { key: 'name', label: 'Gravy Name' },
            { key: 'units', label: 'Units' },
            { key: 'add', label: '', thStyle: 'width: 60px' },
          ]"
          bordered hover show-empty
        >
          <template #cell(units)="row">{{ row.item.uom }} {{ row.item.units }}</template>

          <template #cell(add)="row">
            <div v-if="isGravyInKit(row.item.id)" class="text-center">
              <b-button size="sm" variant="outline-danger" @click="removeItem(getGravyKitItem(row.item.id), 'gravy')">
                <i class="fas fa-minus"></i>
              </b-button>
            </div>
            <div v-else>
              <b-button size="sm" variant="success" :disabled="addingGravyId === row.item.id" @click="addGravyToKit(row.item)">
                <b-spinner v-if="addingGravyId === row.item.id" small></b-spinner>
                <i v-else class="fas fa-plus"></i>
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-2">
          <small class="text-muted">Total: {{ modalGravyTotalRows }} gravies</small>
          <b-pagination
            v-if="modalGravyTotalRows > modalGravyPerPage"
            v-model="modalGravyCurrentPage"
            :total-rows="modalGravyTotalRows"
            :per-page="modalGravyPerPage"
            size="sm" @change="onGravyPageChange"
          ></b-pagination>
        </div>
      </div>
    </b-modal>

    <!-- ── Add Packaging Modal ──────────────────────────────────────────── -->
    <b-modal
      v-model="showPackagingModal"
      title="Add Packaging to Brand Kit"
      size="lg" hide-footer
    >
      <b-form-input
        v-model="modalPackagingFilter"
        placeholder="Search packagings..."
        class="mb-3" @input="onPackagingFilterInput"
      ></b-form-input>

      <div v-if="modalPackagingLoading" class="text-center py-4"><b-spinner variant="primary"></b-spinner></div>
      <div v-else>
        <b-table
          :items="modalPackagings"
          :fields="[
            { key: 'name', label: 'Packaging Name' },
            { key: 'units', label: 'Units' },
            { key: 'add', label: '', thStyle: 'width: 60px' },
          ]"
          bordered hover show-empty
        >
          <template #cell(units)="row">{{ row.item.uom }} {{ row.item.units }}</template>

          <template #cell(add)="row">
            <div v-if="isPackagingInKit(row.item.id)" class="text-center">
              <b-button size="sm" variant="outline-danger" @click="removeItem(getPackagingKitItem(row.item.id), 'packaging')">
                <i class="fas fa-minus"></i>
              </b-button>
            </div>
            <div v-else>
              <b-button size="sm" variant="success" :disabled="addingPackagingId === row.item.id" @click="addPackagingToKit(row.item)">
                <b-spinner v-if="addingPackagingId === row.item.id" small></b-spinner>
                <i v-else class="fas fa-plus"></i>
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center mt-2">
          <small class="text-muted">Total: {{ modalPackagingTotalRows }} items</small>
          <b-pagination
            v-if="modalPackagingTotalRows > modalPackagingPerPage"
            v-model="modalPackagingCurrentPage"
            :total-rows="modalPackagingTotalRows"
            :per-page="modalPackagingPerPage"
            size="sm" @change="onPackagingPageChange"
          ></b-pagination>
        </div>
      </div>
    </b-modal>
  </div>
</template>
